import React, { useState, useEffect } from 'react';
import { PadlockIcon, CircularCheckIcon } from '@assets/icons';
import { isElectron } from '@utils';
import LabelChip from '@components/chips/labelChip';
import { useTheme } from '@utils/hooks/theme';
import { getBoardCompanyColors } from '@utils/theme';
import Text from '@components/text';
import { useUserIsPremium } from '@utils/hooks';
import { useBoardsBySeniority } from '@modules/boards/features/hooks';
import { groupBoardsByGroupPath, getFirstBoardOfEachGroup } from '@modules/boards/features/operations';
import styles from './boardItem.mod.scss';
import { aiModelNames } from '@modules/boards/features/constants';
import ArrowRightIcon from '@assets/icons/arrowRight';

const ELEMENT_TYPE = {
  KIT: 'kit',
  BOOK: 'book',
  ROBOT: 'robot',
  DRONE: 'drone',
  BOARD: 'board',
  MODEL: 'model',
};

const checkIfBoardIsDisabled = (board, userIsPremium) => Boolean(!board || board.enabled === 0 || (board.premium && !userIsPremium));

function findItemsByGroupName(current, groupName) {
  return Object.keys(current)
    .filter(key => key.endsWith(`__${groupName}`))
    .map(key => current[key]);
}

function getCurrentGroup(grouped, path) {
  let current = grouped;
  for (const key of path) {
    current = findItemsByGroupName(current, key)[0];
  }
  return current;
}

function BoardGrid({ onBoardItemClick }) {
  const searchParams = new URLSearchParams(location.search);
  const model = searchParams.get('model') || null;

  const theme = useTheme();
  const boardsBySeniority = useBoardsBySeniority();
  const userIsPremium = useUserIsPremium();
  const [currentBoards, setBoards] = useState([]);
  const [boardSelected, setBoardSelected] = useState(0);
  const [boardMouseOverdId, setboardMouseOverdId] = useState(-1);
  const [groupPath, setGroupPath] = useState([]);
  const [displayPageBack, setDisplayPageBack] = useState(false);

  useEffect(() => {
    if (boardsBySeniority.boards?.length > 0) {
      const groupedBoards = groupBoardsByGroupPath(boardsBySeniority.boards);
      const flattenedBoards = getFirstBoardOfEachGroup(getCurrentGroup(groupedBoards, groupPath));
      setBoards(flattenedBoards);
    }
  }, [boardsBySeniority.boards?.length]);

  useEffect(() => {
    if (model) {
      const translatedModel = aiModelNames[model] || model
      setGroupPath([...groupPath, translatedModel]);
    }
  }, [model]);

  useEffect(() => {
    if (boardsBySeniority.boards?.length > 0) {
      const groupedBoards = groupBoardsByGroupPath(boardsBySeniority.boards);
      const filteredBoards = getFirstBoardOfEachGroup(getCurrentGroup(groupedBoards, groupPath));
      setBoards(filteredBoards);
      selectBoard(filteredBoards[0], 0);
    }
    if ((!model && groupPath.length > 0) || groupPath.length > 1) {
      setDisplayPageBack(true);
    } else {
      setDisplayPageBack(false);
    }
  }, [groupPath.length]);

  useEffect(() => {
    if (currentBoards.length > 0) {
      selectBoard(currentBoards[0], 0);
    }
  }, [currentBoards]);

  const onBackPageClick = () => {
    const currentLevel = groupPath.length;
    const prevGroupingKey = groupPath[currentLevel - 1];
    if (prevGroupingKey) {
      setGroupPath(groupPath.slice(0, -1));
    } else {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }

  const onBoardItemMouseOver = (itemKey, isDisabled) => {
    if (isDisabled) return;
    setboardMouseOverdId(itemKey);
  };

  const onBoardItemMouseLeave = (isDisabled) => {
    if (isDisabled) return;
    setboardMouseOverdId(-1);
  };

  const selectBoard = (board, itemKey) => {
    const isDisabled = checkIfBoardIsDisabled(board, userIsPremium);
    if (isDisabled) return;

    onBoardItemClick({
      ...board,
      boardId: board.id,
    });
    setBoardSelected(itemKey);
  }

  const onBoardClick = (board, itemKey) => {
    const isDisabled = checkIfBoardIsDisabled(board, userIsPremium);
    if (isDisabled) return;

    const currentLevel = groupPath.length;
    const nextGroupingKey = board.groupPath ? board.groupPath[currentLevel] : null;
    if (nextGroupingKey) {
      setGroupPath([...groupPath, nextGroupingKey]);
    }

    selectBoard(board, itemKey);
  };

  return (
    <>
      <div className={styles.navbarContainer}>
        <div className={styles.emptyContainer} />
        <div className={styles.backPageContainer}>
          {displayPageBack && (
            <div className={styles.backArrowWrapper} onClick={onBackPageClick}>
              <ArrowRightIcon />
            </div>
          )}
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.boardGridContainer}>
          {currentBoards.map((board, key) => {
            const isBook = groupPath.length === board.groupPath?.length && board.elementType === ELEMENT_TYPE.BOOK;
            const isMouseOver = key === boardMouseOverdId;
            const isSelected = key === boardSelected;
            const isSelectedOrIsHover = isMouseOver || isSelected;
            const isDisabled = checkIfBoardIsDisabled(board, userIsPremium);
            const boardCompanyNameLower = board.company.toLocaleLowerCase();
            const imageName = board.groupPath?.length > groupPath.length ? board.groupImage || board.image : board.image;
            const imagesPath = `images/profiles/${imageName}`;
            const boardImageUrl = isElectron() ? `./${imagesPath}` : `/${imagesPath}`;
            return (
              <div key={board.id} className={styles.boardContainer}>
                <div
                  className={styles.boardWrapper}
                  onClick={() => onBoardClick(board, key)}
                  onMouseOver={() => onBoardItemMouseOver(key, isDisabled)}
                  onMouseLeave={() => onBoardItemMouseLeave(isDisabled)}
                  style={{
                    background: isSelectedOrIsHover
                      ? getBoardCompanyColors(theme, boardCompanyNameLower)
                      : 'transparent'
                  }}
                >
                  <div className={isDisabled ? styles.boardWrapperDisabled : styles.boardWrapper}>
                    <div className={styles.boardContentContainer}>
                      <div className={styles.boardHeadContainer}>
                        <div className={styles.boardHeadStart} />
                        <div className={isSelected ? styles.boardHead : styles.boardHeadSelected}>
                          {isSelected && <CircularCheckIcon />}
                          {isDisabled && <PadlockIcon />}
                        </div>
                      </div>
                      <div className={styles.boardImageContainer}>
                        <img
                          className={isBook
                            ? styles.boardImageBook
                            : styles.boardImage}
                          src={boardImageUrl}
                          alt="board"
                        />
                      </div>
                    </div>
                    <div className={styles.boardInfoContainer}>
                      <div className={styles.boardInfoWrapper}>
                        <div className={styles.boardNameWrapper}>
                          <Text
                            className={styles.boardName}
                            style={{
                              color: isSelectedOrIsHover
                                ? 'var(--color-white)'
                                : 'var(--color-on-surface)',
                            }}
                          >
                          </Text>
                          <Text className={styles.boardGroup}>
                            {board.groupPath?.length > groupPath.length ? board.groupPath[groupPath.length] || board.name : board.name}
                          </Text>
                        </div>
                        <div className={styles.boardCompanyWrapper}>
                          <div className={styles.boardCompany}>
                            <LabelChip
                              classNames={{
                                container: isSelectedOrIsHover ? styles.boardCompanyChipContainer : '',
                                label: isSelectedOrIsHover ? styles.boardCompanyChipLabel : '',
                              }}
                              color={getBoardCompanyColors(theme, boardCompanyNameLower)}
                              label={board.company}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default BoardGrid;
